import { CreateLevelSchema } from "@/modules/master/domain/contracts";
import { isDevMode } from "@/shared/lib/config";
import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import type { z } from "zod";
import { getRandomMockData, mockLevels } from "../dev-mock-data";

const LevelFormSchema = CreateLevelSchema;
export type LevelFormValues = z.infer<typeof LevelFormSchema>;

export function LevelForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<LevelFormValues>;
  onSubmit: (values: LevelFormValues) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<LevelFormValues>({
    resolver: zodResolver(LevelFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      order: initialValues?.order ?? undefined,
      status: initialValues?.status || "active",
    },
  });

  const handleAutoFill = () => {
    const mockData = getRandomMockData(mockLevels);
    methods.reset(mockData);
  };

  return (
    <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormInput name="name" label="ຊື່" placeholder="ປ້ອນຊື່ລະດັບ" required />

          <FormNumber
            name="order"
            label="ລຳດັບ"
            placeholder="ປ້ອນລຳດັບ (ຈະສ້າງອັດຕະໂນມັດຖ້າບໍ່ກຳນົດ)"
            required
          />
        </div>

        <FormTextarea
          name="description"
          label="ຄຳອະທິບາຍ"
          placeholder="ປ້ອນຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          rows={3}
        />

        <RHF.Controller
          name="status"
          control={methods.control}
          render={({ field }) => (
            <div className="space-y-2">
              <label htmlFor="status" className="font-medium text-sm">
                ສະຖານະ
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={field.value === "active" ? "default" : "outline"}
                  onClick={() => field.onChange("active")}
                >
                  ໃຊ້ງານ
                </Button>
                <Button
                  type="button"
                  variant={field.value === "inactive" ? "default" : "outline"}
                  onClick={() => field.onChange("inactive")}
                >
                  ບໍ່ໃຊ້ງານ
                </Button>
              </div>
            </div>
          )}
        />
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
